import React from 'react';
import { segmentMessageContent, type LinkMatch } from '@/shared/utils/message-link-utils';

interface FormattedMessageContentProps {
  content: string;
  className?: string;
}

/**
 * Component that renders message content with WhatsApp-style link formatting and bullet points.
 * Pure rendering — zero hooks, zero state, zero side effects.
 */
export const FormattedMessageContent: React.FC<FormattedMessageContentProps> = React.memo(({
  content,
  className = ''
}) => {
  const handleLinkClick = (linkData: LinkMatch) => {
    if (linkData.href) {
      window.open(linkData.href, '_blank', 'noopener,noreferrer');
    }
  };

  const getLinkClassName = (type: string) => {
    switch (type) {
      case 'phone':
      case 'email':
        return 'text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 cursor-pointer transition-colors duration-200 underline font-medium';
      case 'time':
        return 'underline decoration-dotted hover:decoration-solid transition-colors duration-200';
      default:
        return '';
    }
  };

  /**
   * Apply WhatsApp-style markdown formatting to text
   * Supports: *bold*, _italic_, ~strikethrough~, `monospace`
   */
  const applyMarkdownFormatting = (text: string) => {
    const parts: React.ReactNode[] = [];
    let keyCounter = 0;

    const patterns = [
      {
        regex: /(\*[^\s*][^*]*?[^\s*]\*|\*[^\s*]\*)/g,
        tag: 'strong',
        className: 'font-semibold'
      },
      {
        regex: /(_[^\s_][^_]*?[^\s_]_|_[^\s_]_)/g,
        tag: 'em',
        className: 'italic'
      },
      {
        regex: /(~[^\s~][^~]*?[^\s~]~|~[^\s~]~)/g,
        tag: 'del',
        className: 'line-through opacity-75'
      },
      {
        regex: /(`[^\s`][^`]*?[^\s`]`|`[^\s`]`)/g,
        tag: 'code',
        className: 'bg-gray-200 dark:bg-gray-700 px-1 rounded text-sm font-mono'
      },
      {
        regex: /\[([^\]]+)\]\([^)]+\)/g,
        tag: 'markdown-link',
        className: 'inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-[#25D366] rounded-lg'
      }
    ];

    const allMatches: Array<{
      start: number;
      end: number;
      match: string;
      tag: string;
      className: string;
    }> = [];

    patterns.forEach(({ regex, tag, className: cls }) => {
      const matches = [...text.matchAll(regex)];
      matches.forEach(match => {
        if (match.index !== undefined) {
          allMatches.push({
            start: match.index,
            end: match.index + match[0].length,
            match: match[0],
            tag,
            className: cls
          });
        }
      });
    });

    allMatches.sort((a, b) => a.start - b.start);

    const filteredMatches = [];
    let lastEnd = 0;
    for (const match of allMatches) {
      if (match.start >= lastEnd) {
        filteredMatches.push(match);
        lastEnd = match.end;
      }
    }

    let currentPos = 0;
    filteredMatches.forEach((match) => {
      if (currentPos < match.start) {
        parts.push(text.slice(currentPos, match.start));
      }

      let innerContent = match.match;
      if (match.tag === 'strong') innerContent = innerContent.slice(1, -1);
      else if (match.tag === 'em') innerContent = innerContent.slice(1, -1);
      else if (match.tag === 'del') innerContent = innerContent.slice(1, -1);
      else if (match.tag === 'code') innerContent = innerContent.slice(1, -1);
      else if (match.tag === 'markdown-link') {
        const linkMatch = match.match.match(/\[([^\]]+)\]/);
        innerContent = linkMatch ? linkMatch[1] : match.match;
      }

      parts.push(
        React.createElement(
          match.tag === 'markdown-link' ? 'span' : match.tag,
          {
            key: `format-${keyCounter++}`,
            className: match.className
          },
          innerContent
        )
      );

      currentPos = match.end;
    });

    if (currentPos < text.length) {
      parts.push(text.slice(currentPos));
    }

    return parts.length > 0 ? parts : [text];
  };

  const renderTextWithLinks = (text: string, keyPrefix: string) => {
    const segments = segmentMessageContent(text);
    return segments.map((segment, segIndex) => {
      if (segment.type === 'link' && segment.linkData) {
        const { linkData } = segment;
        return (
          <span
            key={`${keyPrefix}-link-${segIndex}`}
            className={getLinkClassName(linkData.type)}
            onClick={() => handleLinkClick(linkData)}
            role={linkData.href ? 'button' : undefined}
            tabIndex={linkData.href ? 0 : undefined}
            onKeyDown={(e) => {
              if (linkData.href && (e.key === 'Enter' || e.key === ' ')) {
                e.preventDefault();
                handleLinkClick(linkData);
              }
            }}
          >
            {applyMarkdownFormatting(segment.content)}
          </span>
        );
      }
      return (
        <span key={`${keyPrefix}-text-${segIndex}`}>
          {applyMarkdownFormatting(segment.content)}
        </span>
      );
    });
  };

  const renderContent = () => {
    const processedContent = content
      .replace(/^[\s]*-[\s]+/gm, '• ')
      .replace(/\n{3,}/g, '\n\n');

    const blocks = processedContent.split(/\n\s*\n/).filter(block => block.trim());

    return blocks.map((block, blockIndex) => {
      const lines = block.split('\n').map(line => line.trim()).filter(Boolean);

      const items: Array<{ type: 'bullet' | 'text'; content: string }> = [];
      let currentBulletItem = '';
      let inBulletItem = false;

      for (const line of lines) {
        if (line.startsWith('•')) {
          if (currentBulletItem) {
            items.push({ type: 'bullet', content: currentBulletItem.trim() });
          }
          currentBulletItem = line;
          inBulletItem = true;
        } else if (inBulletItem) {
          currentBulletItem += ' ' + line;
        } else {
          if (currentBulletItem) {
            items.push({ type: 'bullet', content: currentBulletItem.trim() });
            currentBulletItem = '';
            inBulletItem = false;
          }
          items.push({ type: 'text', content: line });
        }
      }

      if (currentBulletItem) {
        items.push({ type: 'bullet', content: currentBulletItem.trim() });
      }

      return (
        <div key={`block-${blockIndex}`} className="mb-2 last:mb-0">
          {items.map((item, itemIndex) => {
            if (item.type === 'bullet') {
              return (
                <div key={`item-${blockIndex}-${itemIndex}`} className="bullet-point">
                  {renderTextWithLinks(item.content, `bullet-${blockIndex}-${itemIndex}`)}
                </div>
              );
            }
            return (
              <div key={`item-${blockIndex}-${itemIndex}`} className="mb-1 last:mb-0">
                {renderTextWithLinks(item.content, `text-${blockIndex}-${itemIndex}`)}
              </div>
            );
          })}
        </div>
      );
    });
  };

  return (
    <div className={`message-content select-text ${className}`}>
      {renderContent()}
    </div>
  );
});

FormattedMessageContent.displayName = 'FormattedMessageContent';
